import React, { ReactNode, Fragment, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Popover, Dialog, Transition } from '@headlessui/react';
import { useAuth } from '../../hooks/useAuth';
import { MessageSquare, FileText, LogOut, User, Shield, Sun, Moon, Globe, UsersRound, GitCompare, MoreHorizontal, Volume2 } from 'lucide-react';
import { useDarkMode } from '../../hooks/useDarkMode';
import { useVoicePreferences } from '../../hooks/useVoicePreferences';
import { useSpeechSynthesis } from '../../hooks/useSpeechSynthesis';
import clsx from 'clsx';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout, canManageUsers } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { effectiveTheme, toggle } = useDarkMode();
  const { autoRead, setAutoRead } = useVoicePreferences(user?.id ?? null);
  const { supported: ttsSupported, speak } = useSpeechSynthesis();
  const [moreOpen, setMoreOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleAutoRead = () => {
    const next = !autoRead;
    setAutoRead(next);
    // Audible confirmation inside this user gesture so the tech learns on the
    // spot whether their device actually speaks (spec §6.3).
    if (next && ttsSupported) speak('Auto read on');
  };

  const isUserAdmin = canManageUsers();

  // Primary destinations stay pinned on the mobile tab bar; the rest fold into
  // a "More" sheet so the bar never overflows (admins otherwise had 6+ tabs).
  const primaryNav = [
    { name: 'Chat', href: '/chat', icon: MessageSquare },
    { name: 'Documents', href: '/documents', icon: FileText },
    ...(user?.is_admin ? [{ name: 'Admin', href: '/admin', icon: Shield }] : []),
  ];
  const overflowNav = isUserAdmin
    ? [
        { name: 'Users', href: '/admin/users', icon: UsersRound },
        { name: 'Domains', href: '/admin/allowed-domains', icon: Globe },
        { name: 'Deploys', href: '/admin/deploys', icon: GitCompare },
      ]
    : [];

  // Desktop icon rail shows the full, flat list.
  const navigation = [...primaryNav, ...overflowNav];

  const isActive = (href: string) => {
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen-dvh bg-gray-50 dark:bg-gray-950">
      {/* Desktop icon rail */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-16 lg:flex-col lg:z-30">
        <div className="flex flex-col items-center min-h-0 flex-1 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700">
          <Link
            to="/chat"
            className="flex items-center justify-center h-16 w-16 border-b border-gray-200 dark:border-gray-700"
            aria-label="AIRAssist home"
          >
            <img
              src="/AIRlogo.png"
              alt="AIR Control Concepts"
              className="max-w-[44px] h-auto dark:brightness-0 dark:invert"
            />
          </Link>

          <nav className="flex-1 flex flex-col items-center py-4 space-y-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                title={item.name}
                aria-label={item.name}
                className={clsx(
                  'flex items-center justify-center w-10 h-10 rounded-md transition-colors',
                  isActive(item.href)
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                )}
              >
                <item.icon className="h-5 w-5" />
              </Link>
            ))}
          </nav>

          <div className="py-3 border-t border-gray-200 dark:border-gray-700 w-full flex justify-center">
            <Popover className="relative">
              {({ open }) => (
                <>
                  <Popover.Button
                    className={clsx(
                      'flex items-center justify-center w-10 h-10 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500',
                      open
                        ? 'bg-primary-200 dark:bg-primary-900/50'
                        : 'bg-primary-100 hover:bg-primary-200 dark:bg-primary-900/30 dark:hover:bg-primary-900/50'
                    )}
                    aria-label="User menu"
                  >
                    <User className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </Popover.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-75"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Popover.Panel className="absolute left-14 bottom-0 w-64 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-3 z-50">
                      <div className="px-2 py-2">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {user?.full_name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {user?.email}
                        </p>
                      </div>
                      <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2 space-y-1">
                        <button
                          onClick={toggle}
                          className="flex items-center w-full px-2 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          {effectiveTheme === 'dark' ? (
                            <Sun className="mr-3 h-4 w-4" />
                          ) : (
                            <Moon className="mr-3 h-4 w-4" />
                          )}
                          {effectiveTheme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                        </button>
                        {ttsSupported && (
                          <button
                            onClick={toggleAutoRead}
                            className="flex items-center justify-between w-full px-2 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
                          >
                            <span className="flex items-center">
                              <Volume2 className="mr-3 h-4 w-4" />
                              Auto-read answers
                            </span>
                            <span
                              className={clsx(
                                'text-xs px-2 py-0.5 rounded-full',
                                autoRead
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                  : 'bg-gray-100 text-gray-500 dark:bg-gray-800'
                              )}
                            >
                              {autoRead ? 'On' : 'Off'}
                            </span>
                          </button>
                        )}
                        <button
                          onClick={handleLogout}
                          className="flex items-center w-full px-2 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <LogOut className="mr-3 h-4 w-4" />
                          Sign out
                        </button>
                      </div>
                    </Popover.Panel>
                  </Transition>
                </>
              )}
            </Popover>
          </div>
        </div>
      </div>

      {/* Mobile bottom tab bar: pinned primary tabs + a "More" sheet trigger.
          Each tab is min-w-0 + truncate so labels can never push the row wider
          than the viewport (the old all-tabs row overflowed for admins). */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 safe-bottom">
        <div className="flex items-stretch justify-around h-14">
          {primaryNav.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={clsx(
                'flex flex-col items-center justify-center flex-1 min-w-0 h-full text-xs',
                isActive(item.href)
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-gray-500 dark:text-gray-400'
              )}
            >
              <item.icon className="h-5 w-5 mb-1 flex-shrink-0" />
              <span className="truncate max-w-full px-1">{item.name}</span>
            </Link>
          ))}
          <button
            onClick={() => setMoreOpen(true)}
            aria-label="More"
            className={clsx(
              'flex flex-col items-center justify-center flex-1 min-w-0 h-full text-xs',
              moreOpen ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'
            )}
          >
            <MoreHorizontal className="h-5 w-5 mb-1 flex-shrink-0" />
            <span className="truncate max-w-full px-1">More</span>
          </button>
        </div>
      </div>

      {/* Mobile "More" bottom sheet: overflow nav items + theme toggle */}
      <Transition.Root show={moreOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setMoreOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-900/40" />
          </Transition.Child>

          <div className="fixed inset-x-0 bottom-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-out duration-300 transform"
              enterFrom="translate-y-full"
              enterTo="translate-y-0"
              leave="transition ease-in duration-200 transform"
              leaveFrom="translate-y-0"
              leaveTo="translate-y-full"
            >
              <Dialog.Panel className="w-full bg-white dark:bg-gray-900 rounded-t-2xl border-t border-gray-200 dark:border-gray-700 shadow-2xl safe-bottom">
                {/* Grabber */}
                <div className="flex justify-center pt-3 pb-1">
                  <div className="h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-700" />
                </div>
                <div className="px-3 pt-1 pb-3 space-y-1">
                  {overflowNav.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMoreOpen(false)}
                      className={clsx(
                        'flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium transition-colors',
                        isActive(item.href)
                          ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                      )}
                    >
                      <item.icon className="h-5 w-5 flex-shrink-0" />
                      <span className="truncate">{item.name}</span>
                    </Link>
                  ))}
                  <button
                    onClick={toggle}
                    className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    {effectiveTheme === 'dark' ? (
                      <Sun className="h-5 w-5 flex-shrink-0" />
                    ) : (
                      <Moon className="h-5 w-5 flex-shrink-0" />
                    )}
                    <span>{effectiveTheme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>
                  </button>
                  {ttsSupported && (
                    <button
                      onClick={toggleAutoRead}
                      className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <span className="flex items-center gap-3">
                        <Volume2 className="h-5 w-5 flex-shrink-0" />
                        Auto-read answers
                      </span>
                      <span
                        className={clsx(
                          'text-xs px-2 py-0.5 rounded-full',
                          autoRead
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-800'
                        )}
                      >
                        {autoRead ? 'On' : 'Off'}
                      </span>
                    </button>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <LogOut className="h-5 w-5 flex-shrink-0" />
                    <span>Sign out</span>
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Main content. h-screen-dvh tracks the visible viewport on mobile;
          overflow-x-hidden guards against any stray horizontal scroll. */}
      <div className="lg:pl-16 h-screen-dvh flex flex-col overflow-x-hidden">
        <main className="flex-1 overflow-hidden pb-mobile-nav lg:pb-0">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
